import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";
import {
  setBrandsData,
  setBrandsLoading,
  setBrandsMtdData,
  setBrandsMtdLoading,
  TableSort,
} from "../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { CsvSection, sortRowsForExport } from "../csvExport";
import { useOptifundTabData } from "../hooks/useOptifundTabData";
import {
  BRANDS_DAILY_CSV_HEADERS,
  BRANDS_MTD_CSV_HEADERS,
  BrandDailyRow,
  BrandMtdRow,
  mapBrandDailyApiRow,
  mapBrandMtdApiRow,
  toBrandsDailyCsvRow,
  toBrandsMtdCsvRow,
} from "./brandsApiMapping";
import BrandsFilterCard from "./BrandsFilterCard";
import BrandsKpiCards from "./BrandsKpiCards";
import BrandsIncomeChart from "./BrandsIncomeChart";
import BrandsDailyTable from "./BrandsDailyTable";
import BrandsMtdTable from "./BrandsMtdTable";
import BrandsSkeleton from "./BrandsSkeleton";

const ALL_BRANDS = "All brands";
const EMPTY_DAILY_ROWS: BrandDailyRow[] = [];
const EMPTY_MTD_ROWS: BrandMtdRow[] = [];
const DEFAULT_DAILY_SORT: TableSort = { keytoSort: "date", direction: "desc" };
const DEFAULT_MTD_SORT: TableSort = { keytoSort: "brand", direction: "asc" };
const PAGE_SIZE_OPTIONS = [10, 25, 100];

interface BrandsProps {
  userToken: any;
  websiteSharedUid: string;
}

export interface BrandsExportHandle {
  getExportSections: () => CsvSection[] | null;
}

function readPage(searchParams: URLSearchParams, key: string): number {
  const value = Number(searchParams.get(key));
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

function readPageSize(searchParams: URLSearchParams, key: string): number {
  const value = Number(searchParams.get(key));
  return PAGE_SIZE_OPTIONS.includes(value) ? value : 10;
}

function readSort(searchParams: URLSearchParams, sortKey: string, dirKey: string, fallback: TableSort): TableSort {
  const key = searchParams.get(sortKey);
  const direction = searchParams.get(dirKey);
  return key && (direction === "asc" || direction === "desc") ? { keytoSort: key, direction } : fallback;
}

const Brands = forwardRef<BrandsExportHandle, BrandsProps>(function Brands(
  { userToken, websiteSharedUid },
  ref
) {
  const dispatch = useDispatch();
  const { brandsData, brandsLoading, brandsMtdData, brandsMtdLoading } = useSelector(
    (state: any) => state.optifundDashboard
  );

  // Filter/sort/pagination state for this tab lives here (not Redux) and is
  // synced with the URL directly via useSearchParams - see
  // docs/new-module-checklist.md Section 5/8.12.
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedBrand, setSelectedBrand] = useState(() => searchParams.get("brand") ?? ALL_BRANDS);
  const [dailyPage, setDailyPage] = useState(() => readPage(searchParams, "brands_daily_page"));
  const [dailyRowsPerPage, setDailyRowsPerPage] = useState(() =>
    readPageSize(searchParams, "brands_daily_page_size")
  );
  const [dailySort, setDailySort] = useState<TableSort>(() =>
    readSort(searchParams, "brands_daily_sort", "brands_daily_dir", DEFAULT_DAILY_SORT)
  );
  const [mtdPage, setMtdPage] = useState(() => readPage(searchParams, "brands_mtd_page"));
  const [mtdRowsPerPage, setMtdRowsPerPage] = useState(() => readPageSize(searchParams, "brands_mtd_page_size"));
  const [mtdSort, setMtdSort] = useState<TableSort>(() =>
    readSort(searchParams, "brands_mtd_sort", "brands_mtd_dir", DEFAULT_MTD_SORT)
  );

  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (selectedBrand === ALL_BRANDS) next.delete("brand");
        else next.set("brand", selectedBrand);

        if (dailyPage === 0) next.delete("brands_daily_page");
        else next.set("brands_daily_page", String(dailyPage));
        if (dailyRowsPerPage === 10) next.delete("brands_daily_page_size");
        else next.set("brands_daily_page_size", String(dailyRowsPerPage));
        if (dailySort.keytoSort === DEFAULT_DAILY_SORT.keytoSort && dailySort.direction === DEFAULT_DAILY_SORT.direction) {
          next.delete("brands_daily_sort");
          next.delete("brands_daily_dir");
        } else {
          next.set("brands_daily_sort", dailySort.keytoSort);
          next.set("brands_daily_dir", dailySort.direction);
        }

        if (mtdPage === 0) next.delete("brands_mtd_page");
        else next.set("brands_mtd_page", String(mtdPage));
        if (mtdRowsPerPage === 10) next.delete("brands_mtd_page_size");
        else next.set("brands_mtd_page_size", String(mtdRowsPerPage));
        if (mtdSort.keytoSort === DEFAULT_MTD_SORT.keytoSort && mtdSort.direction === DEFAULT_MTD_SORT.direction) {
          next.delete("brands_mtd_sort");
          next.delete("brands_mtd_dir");
        } else {
          next.set("brands_mtd_sort", mtdSort.keytoSort);
          next.set("brands_mtd_dir", mtdSort.direction);
        }
        return next;
      },
      { replace: true }
    );
  }, [selectedBrand, dailyPage, dailyRowsPerPage, dailySort, mtdPage, mtdRowsPerPage, mtdSort, setSearchParams]);

  const handleDailyRowsPerPageChange = (nextRowsPerPage: number) => {
    setDailyRowsPerPage(nextRowsPerPage);
    setDailyPage(0);
  };

  const handleMtdRowsPerPageChange = (nextRowsPerPage: number) => {
    setMtdRowsPerPage(nextRowsPerPage);
    setMtdPage(0);
  };

  // Both queries require a `brand` param same as website_shared_uid -
  // always fetching "all" here and filtering client-side (below) keeps
  // brand-switching instant and lets the filter dropdown's options be
  // derived from the real brand names in the response.
  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsDailyId,
    (data) => dispatch(setBrandsData(data)),
    (loading) => dispatch(setBrandsLoading(loading)),
    undefined,
    undefined,
    "all"
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsMtdId,
    (data) => dispatch(setBrandsMtdData(data)),
    (loading) => dispatch(setBrandsMtdLoading(loading)),
    undefined,
    undefined,
    "all"
  );

  const dailyRows: BrandDailyRow[] = useMemo(
    () => (Array.isArray(brandsData) ? brandsData.map(mapBrandDailyApiRow) : EMPTY_DAILY_ROWS),
    [brandsData]
  );

  const mtdRows: BrandMtdRow[] = useMemo(
    () => (Array.isArray(brandsMtdData) ? brandsMtdData.map(mapBrandMtdApiRow) : EMPTY_MTD_ROWS),
    [brandsMtdData]
  );

  const brandOptions = useMemo(() => {
    const uniqueBrands = Array.from(
      new Set([...dailyRows.map((row) => row.brand), ...mtdRows.map((row) => row.brand)])
    ).sort();
    return [ALL_BRANDS, ...uniqueBrands];
  }, [dailyRows, mtdRows]);

  const filteredDailyRows = useMemo(
    () => dailyRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [dailyRows, selectedBrand]
  );

  const filteredMtdRows = useMemo(
    () => mtdRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [mtdRows, selectedBrand]
  );

  const isLoading =
    brandsLoading || brandsMtdLoading || !Array.isArray(brandsData) || !Array.isArray(brandsMtdData);

  useImperativeHandle(
    ref,
    () => ({
      getExportSections: (): CsvSection[] | null => {
        if (isLoading) return null;
        return [
          {
            title: "Brands - Daily",
            headers: BRANDS_DAILY_CSV_HEADERS,
            rows: sortRowsForExport(filteredDailyRows, dailySort).map(toBrandsDailyCsvRow),
          },
          {
            title: "Brands - MTD",
            headers: BRANDS_MTD_CSV_HEADERS,
            rows: sortRowsForExport(filteredMtdRows, mtdSort).map(toBrandsMtdCsvRow),
          },
        ];
      },
    }),
    [isLoading, filteredDailyRows, filteredMtdRows, dailySort, mtdSort]
  );

  if (isLoading) {
    return <BrandsSkeleton />;
  }

  return (
    <Stack spacing={2.5}>
      <BrandsFilterCard
        brandOptions={brandOptions}
        selectedBrand={selectedBrand}
        onSelectedBrandChange={setSelectedBrand}
      />
      <BrandsKpiCards dailyRows={filteredDailyRows} mtdRows={filteredMtdRows} />
      <BrandsIncomeChart rows={filteredMtdRows} />
      <BrandsDailyTable
        rows={filteredDailyRows}
        page={dailyPage}
        onPageChange={setDailyPage}
        rowsPerPage={dailyRowsPerPage}
        onRowsPerPageChange={handleDailyRowsPerPageChange}
        sort={dailySort}
        onSortChange={setDailySort}
      />
      <BrandsMtdTable
        rows={filteredMtdRows}
        page={mtdPage}
        onPageChange={setMtdPage}
        rowsPerPage={mtdRowsPerPage}
        onRowsPerPageChange={handleMtdRowsPerPageChange}
        sort={mtdSort}
        onSortChange={setMtdSort}
      />
    </Stack>
  );
});

export default Brands;
